import {useCallback} from 'react';

import {PROJECT_TAGS} from '@/constants/ProjectsConstants';
import {useTrackingManager} from '@/hooks';
import {
  IErrorDataDefinition,
  IMoDataDefinition,
  ISplitsDataDefinition,
} from '@/services/segment/models/TrackingManagerEventModel';

import {GROUPS_EVENTS} from './events/GroupsEvents';

interface IGroupsTrackEventProps {
  event: string;
  metadata?: Record<string, unknown>;
  splits?: Partial<ISplitsDataDefinition>;
  error?: IErrorDataDefinition;
  mo?: IMoDataDefinition;
}

export interface ISelectSplitsAddExpenseConfirmParams {
  groupId: string;
  groupName: string;
  expenseTitle: string;
  expenseAmount: string;
  expenseDate: string;
  expensePayerId: string;
  error?: IErrorDataDefinition;
}

export const useGroupsTrackers = (
  groupId: string,
  ownerId?: string,
  userId?: string,
) => {
  const {trackEvent} = useTrackingManager();

  const trackGroupsEvent = useCallback(
    (props: IGroupsTrackEventProps) => {
      const {event, metadata, splits, error, mo} = props;
      trackEvent(event, {
        project_tag: PROJECT_TAGS.GROUPS,
        ...(ownerId && {
          user: {id: ownerId},
        }),
        ...(userId && {
          user: {id: userId},
        }),
        metadata,
        Splits: {
          group: {
            id: groupId,
            ...(splits?.group && {...splits.group}),
          },
          ...(splits?.expense && {expense: {...splits.expense}}),
        },
        ...(error && {error}),
        ...(mo && {Mo: mo}),
      });
    },
    [trackEvent, ownerId, userId, groupId],
  );

  const trackViewSplitsJoinGroup = useCallback(
    (
      groupId?: string | null,
      groupName?: string | null,
      groupEmoji?: string | null,
    ) => {
      trackGroupsEvent({
        event: GROUPS_EVENTS.VIEW_SPLITS_JOIN_GROUP,
        splits: {
          group: {
            id: groupId ?? undefined,
            name: groupName ?? undefined,
            emoji: groupEmoji ?? undefined,
          },
        },
      });
    },
    [trackGroupsEvent],
  );

  const trackSelectSplitsJoinGroup = useCallback(
    (
      groupId?: string | null,
      groupName?: string | null,
      groupEmoji?: string | null,
    ) => {
      trackGroupsEvent({
        event: GROUPS_EVENTS.SELECT_SPLITS_JOIN_GROUP,
        splits: {
          group: {
            id: groupId ?? undefined,
            name: groupName ?? undefined,
            emoji: groupEmoji ?? undefined,
          },
        },
      });
    },
    [trackGroupsEvent],
  );

  const trackSelectSplitsSelectGroupMember = useCallback(() => {
    trackGroupsEvent({
      event: GROUPS_EVENTS.SELECT_SPLITS_SELECT_GROUP_MEMBER,
    });
  }, [trackGroupsEvent]);

  const trackSelectSplitsMemberNotListed = useCallback(() => {
    trackGroupsEvent({
      event: GROUPS_EVENTS.SELECT_SPLITS_MEMBER_NOT_LISTED,
    });
  }, [trackGroupsEvent]);

  const trackSelectSplitsFirstMemberWritesName = useCallback(() => {
    trackGroupsEvent({
      event: GROUPS_EVENTS.SELECT_SPLITS_FIRST_MEMBER_WRITES_NAME,
    });
  }, [trackGroupsEvent]);

  const trackViewSplitsJoinGroupFirstTime = useCallback(() => {
    trackGroupsEvent({
      event: GROUPS_EVENTS.VIEW_SPLITS_JOIN_GROUP_FIRST_TIME,
    });
  }, [trackGroupsEvent]);

  const trackSelectSplitsPhoneNumberContinue = useCallback(() => {
    trackGroupsEvent({
      event: GROUPS_EVENTS.SELECT_SPLITS_PHONE_NUMBER_CONTINUE,
    });
  }, [trackGroupsEvent]);

  const trackViewSplitsOtpEnterCode = useCallback(() => {
    trackGroupsEvent({
      event: GROUPS_EVENTS.VIEW_SPLITS_OTP_ENTER_CODE,
    });
  }, [trackGroupsEvent]);

  const trackSelectSplitsOtpEnterCode = useCallback(() => {
    trackGroupsEvent({
      event: GROUPS_EVENTS.SELECT_SPLITS_OTP_ENTER_CODE,
    });
  }, [trackGroupsEvent]);

  const trackApiSplitsOtpSuccess = useCallback(() => {
    trackGroupsEvent({
      event: GROUPS_EVENTS.API_SPLITS_OTP_SUCCESS,
    });
  }, [trackGroupsEvent]);

  const trackApiSplitsOtpError = useCallback(
    (error?: string, code?: string) => {
      trackGroupsEvent({
        event: GROUPS_EVENTS.API_SPLITS_OTP_ERROR,
        error: {
          code: code ?? 'unknown_error',
          message: error ?? 'An unknown error occurred',
        },
      });
    },
    [trackGroupsEvent],
  );

  const trackViewSplitsGroup = useCallback(
    (groupId?: string | null) => {
      trackGroupsEvent({
        event: GROUPS_EVENTS.VIEW_SPLITS_GROUP,
        metadata: {
          group_id: groupId ?? undefined,
        },
      });
    },
    [trackGroupsEvent],
  );

  const trackSelectSplitsInviteMember = useCallback(() => {
    trackGroupsEvent({
      event: GROUPS_EVENTS.SELECT_SPLITS_INVITE_MEMBER,
    });
  }, [trackGroupsEvent]);

  const trackSelectSplitsAddExpense = useCallback(() => {
    trackGroupsEvent({
      event: GROUPS_EVENTS.SELECT_SPLITS_ADD_EXPENSE,
    });
  }, [trackGroupsEvent]);

  const trackSelectSplitsAddExpenseConfirm = useCallback(
    ({
      groupId,
      groupName,
      expenseTitle,
      expenseAmount,
      expenseDate,
      expensePayerId,
    }: ISelectSplitsAddExpenseConfirmParams) => {
      trackGroupsEvent({
        event: GROUPS_EVENTS.SELECT_SPLITS_ADD_EXPENSE_CONFIRM,
        splits: {
          group: {
            id: groupId,
            name: groupName,
          },
          expense: {
            title: expenseTitle,
            amount: expenseAmount,
            date: expenseDate,
            payer: {
              id: expensePayerId,
            },
          },
        },
      });
    },
    [trackGroupsEvent],
  );

  const trackApiSplitsAddExpenseSuccess = useCallback(
    ({
      groupId,
      groupName,
      expenseTitle,
      expenseAmount,
      expenseDate,
      expensePayerId,
    }: ISelectSplitsAddExpenseConfirmParams) => {
      trackGroupsEvent({
        event: GROUPS_EVENTS.API_SPLITS_ADD_EXPENSE_SUCCESS,
        splits: {
          group: {
            id: groupId,
            name: groupName,
          },
          expense: {
            title: expenseTitle,
            amount: expenseAmount,
            date: expenseDate,
            payer: {
              id: expensePayerId,
            },
          },
        },
      });
    },
    [trackGroupsEvent],
  );

  const trackApiSplitsAddExpenseError = useCallback(
    ({
      groupId,
      groupName,
      expenseTitle,
      expenseAmount,
      expenseDate,
      expensePayerId,
      error,
    }: ISelectSplitsAddExpenseConfirmParams) => {
      trackGroupsEvent({
        event: GROUPS_EVENTS.API_SPLITS_ADD_EXPENSE_ERROR,
        splits: {
          group: {
            id: groupId,
            name: groupName,
          },
          expense: {
            title: expenseTitle,
            amount: expenseAmount,
            date: expenseDate,
            payer: {
              id: expensePayerId,
            },
          },
        },
        error: error ?? {
          code: 'unknown_error',
          message: 'An unknown error occurred',
        },
      });
    },
    [trackGroupsEvent],
  );

  const trackSelectSplitsYouAreOwed = useCallback(() => {
    trackGroupsEvent({
      event: GROUPS_EVENTS.SELECT_SPLITS_YOU_ARE_OWED,
    });
  }, [trackGroupsEvent]);

  const trackSelectSplitsYouOwe = useCallback(() => {
    trackGroupsEvent({
      event: GROUPS_EVENTS.SELECT_SPLITS_YOU_OWE,
    });
  }, [trackGroupsEvent]);

  const trackSelectSplitsSettleOneDebt = useCallback(() => {
    trackGroupsEvent({
      event: GROUPS_EVENTS.SELECT_SPLITS_SETTLE_ONE_DEBT,
    });
  }, [trackGroupsEvent]);

  const trackSelectSplitsSettleConfirmReceived = useCallback(() => {
    trackGroupsEvent({
      event: GROUPS_EVENTS.SELECT_SPLITS_SETTLE_CONFIRM_RECEIVED,
    });
  }, [trackGroupsEvent]);

  const trackSelectSplitsSettleMarkAsPaid = useCallback(() => {
    trackGroupsEvent({
      event: GROUPS_EVENTS.SELECT_SPLITS_SETTLE_MARK_AS_PAID,
    });
  }, [trackGroupsEvent]);

  const trackSelectSplitsSettlePayMember = useCallback(() => {
    trackGroupsEvent({
      event: GROUPS_EVENTS.SELECT_SPLITS_SETTLE_PAY_MEMBER,
    });
  }, [trackGroupsEvent]);

  const trackSelectSplitsSettleAll = useCallback(() => {
    trackGroupsEvent({
      event: GROUPS_EVENTS.SELECT_SPLITS_SETTLE_ALL,
    });
  }, [trackGroupsEvent]);

  const trackSelectSplitsSettleAllMarkAsPaid = useCallback(() => {
    trackGroupsEvent({
      event: GROUPS_EVENTS.SELECT_SPLITS_SETTLE_ALL_MARK_AS_PAID,
    });
  }, [trackGroupsEvent]);

  const trackSelectSplitsSettleAllConfirmReceived = useCallback(() => {
    trackGroupsEvent({
      event: GROUPS_EVENTS.SELECT_SPLITS_SETTLE_ALL_CONFIRM_RECEIVED,
    });
  }, [trackGroupsEvent]);

  const trackSelectSplitsGetModak = useCallback(() => {
    trackGroupsEvent({
      event: GROUPS_EVENTS.SELECT_SPLITS_GET_MODAK,
    });
  }, [trackGroupsEvent]);

  const trackSelectSplitsControl = useCallback(
    (control: string) => {
      trackGroupsEvent({
        event: GROUPS_EVENTS.SELECT_SPLITS_CONTROL,
        metadata: {
          control,
        },
      });
    },
    [trackGroupsEvent],
  );

  const trackApiSplitsGetActivitySuccess = useCallback(
    (groupId?: string | null) => {
      trackGroupsEvent({
        event: GROUPS_EVENTS.API_SPLITS_GET_ACTIVITY_SUCCESS,
        splits: {
          group: {
            id: groupId ?? undefined,
          },
        },
      });
    },
    [trackGroupsEvent],
  );

  const trackApiSplitsGetActivityError = useCallback(
    (groupId?: string | null, error?: string, code?: string) => {
      trackGroupsEvent({
        event: GROUPS_EVENTS.API_SPLITS_GET_ACTIVITY_ERROR,
        splits: {
          group: {
            id: groupId ?? undefined,
          },
        },
        error: {
          code: code ?? 'unknown_error',
          message: error ?? 'An unknown error occurred',
        },
      });
    },
    [trackGroupsEvent],
  );

  const trackViewMoResponseError = useCallback(
    (error?: string, code?: string) => {
      trackGroupsEvent({
        event: GROUPS_EVENTS.VIEW_MO_RESPONSE_ERROR,
        error: {
          code: code ?? 'unknown_error',
          message: error ?? 'An unknown error occurred',
        },
      });
    },
    [trackGroupsEvent],
  );

  const trackViewMoMessageReceived = useCallback(
    (messageType?: string | null, messageText?: string | null) => {
      trackGroupsEvent({
        event: GROUPS_EVENTS.VIEW_MO_MESSAGE_RECEIVED,
        mo: {
          ai: {
            message: {
              type: messageType ?? undefined,
              text: messageText ?? undefined,
            },
          },
        },
      });
    },
    [trackGroupsEvent],
  );

  const trackSelectMoMessageSent = useCallback(
    (messageText?: string | null) => {
      trackGroupsEvent({
        event: GROUPS_EVENTS.SELECT_MO_MESSAGE_SENT,
        mo: {
          ai: {
            message: {
              text: messageText ?? undefined,
            },
          },
        },
      });
    },
    [trackGroupsEvent],
  );

  const trackViewMoSplitProposal = useCallback(
    (groupId?: string | null, type?: string | null) => {
      trackGroupsEvent({
        event: GROUPS_EVENTS.VIEW_MO_SPLIT_PROPOSAL,
        splits: {
          group: {
            id: groupId ?? undefined,
            type: type ?? undefined,
          },
        },
      });
    },
    [trackGroupsEvent],
  );

  const trackSelectMoSplitProposalUndo = useCallback(
    (groupId?: string | null, type?: string | null) => {
      trackGroupsEvent({
        event: GROUPS_EVENTS.SELECT_MO_SPLIT_PROPOSAL_UNDO,
        splits: {
          group: {
            id: groupId ?? undefined,
            type: type ?? undefined,
          },
        },
      });
    },
    [trackGroupsEvent],
  );

  const trackSelectMoSplitProposalConfirm = useCallback(
    (groupId?: string | null, type?: string | null) => {
      trackGroupsEvent({
        event: GROUPS_EVENTS.SELECT_MO_SPLIT_PROPOSAL_CONFIRM,
        splits: {
          group: {
            id: groupId ?? undefined,
            type: type ?? undefined,
          },
        },
      });
    },
    [trackGroupsEvent],
  );

  const trackApiMoAddSplitSuccess = useCallback(
    (groupId?: string | null, type?: string | null) => {
      trackGroupsEvent({
        event: GROUPS_EVENTS.API_MO_ADD_SPLIT_SUCCESS,
        splits: {
          group: {
            id: groupId ?? undefined,
            type: type ?? undefined,
          },
        },
      });
    },
    [trackGroupsEvent],
  );

  const trackApiMoAddSplitError = useCallback(
    (
      groupId?: string | null,
      type?: string | null,
      error?: string,
      code?: string,
    ) => {
      trackGroupsEvent({
        event: GROUPS_EVENTS.API_MO_ADD_SPLIT_ERROR,
        splits: {
          group: {
            id: groupId ?? undefined,
            type: type ?? undefined,
          },
        },
        error: {
          code: code ?? 'unknown_error',
          message: error ?? 'An unknown error occurred',
        },
      });
    },
    [trackGroupsEvent],
  );

  const trackSelectMoCopySplitLink = useCallback(() => {
    trackGroupsEvent({
      event: GROUPS_EVENTS.SELECT_MO_COPY_SPLIT_LINK,
    });
  }, [trackGroupsEvent]);

  const trackSelectMoShareSplitLink = useCallback(() => {
    trackGroupsEvent({
      event: GROUPS_EVENTS.SELECT_MO_SHARE_SPLIT_LINK,
    });
  }, [trackGroupsEvent]);

  const trackShareMoSplitLinkSuccess = useCallback(() => {
    trackGroupsEvent({
      event: GROUPS_EVENTS.SHARE_MO_SPLIT_LINK_SUCCESS,
    });
  }, [trackGroupsEvent]);

  return {
    trackViewSplitsJoinGroup,
    trackSelectSplitsJoinGroup,
    trackSelectSplitsSelectGroupMember,
    trackSelectSplitsMemberNotListed,
    trackSelectSplitsFirstMemberWritesName,
    trackViewSplitsJoinGroupFirstTime,
    trackSelectSplitsPhoneNumberContinue,
    trackViewSplitsOtpEnterCode,
    trackSelectSplitsOtpEnterCode,
    trackApiSplitsOtpSuccess,
    trackApiSplitsOtpError,
    trackViewSplitsGroup,
    trackSelectSplitsInviteMember,
    trackSelectSplitsAddExpense,
    trackSelectSplitsAddExpenseConfirm,
    trackApiSplitsAddExpenseSuccess,
    trackApiSplitsAddExpenseError,
    trackSelectSplitsYouAreOwed,
    trackSelectSplitsYouOwe,
    trackSelectSplitsSettleOneDebt,
    trackSelectSplitsSettleConfirmReceived,
    trackSelectSplitsSettleMarkAsPaid,
    trackSelectSplitsSettlePayMember,
    trackSelectSplitsSettleAll,
    trackSelectSplitsSettleAllMarkAsPaid,
    trackSelectSplitsSettleAllConfirmReceived,
    trackSelectSplitsGetModak,
    trackSelectSplitsControl,
    trackApiSplitsGetActivitySuccess,
    trackApiSplitsGetActivityError,
    trackViewMoResponseError,
    trackViewMoMessageReceived,
    trackSelectMoMessageSent,
    trackViewMoSplitProposal,
    trackSelectMoSplitProposalUndo,
    trackSelectMoSplitProposalConfirm,
    trackApiMoAddSplitSuccess,
    trackApiMoAddSplitError,
    trackSelectMoCopySplitLink,
    trackSelectMoShareSplitLink,
    trackShareMoSplitLinkSuccess,
  };
};
